import React, { useState } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import CourtTypeManagement from './CourtTypeManagement';
import CourtManagement from './CourtManagement';
import BookingManagement from './BookingManagement';
import AdminBooking from './AdminBooking';
import UserManagement from './UserManagement';
import ClosureManagement from './ClosureManagement';
import Dashboard from './Dashboard';

const AdminDashboard = () => {
  const { adminUser, loading, error, isAdmin } = useAdmin();
  const [activeTab, setActiveTab] = useState('courts');

  // Show loading while checking admin status
  if ((loading || !adminUser) && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังตรวจสอบสิทธิ์ผู้ดูแลระบบ...</p>
        </div>
      </div>
    );
  }

  // Show access denied only after admin check is complete and user is not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">ต้องมีสิทธิ์ผู้ดูแลระบบ</h2>
          <p className="text-gray-600 mb-4">คุณไม่มีสิทธิ์เข้าถึงแผงควบคุมผู้ดูแลระบบ</p>
          <p className="text-sm text-gray-500">กรุณาติดต่อผู้ดูแลระบบเพื่อขอสิทธิ์เข้าถึง</p>
        </div>
      </div>
    );
  }

  // Show error if there was an issue during admin check
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">กรุณาลองรีเฟรชหน้าหรือติดต่อฝ่ายสนับสนุน</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'courts', name: 'สนาม', icon: '🏟️' },
    { id: 'court-types', name: 'ประเภทสนาม', icon: '🏀' },
    { id: 'bookings', name: 'การจอง', icon: '📅' },
    { id: 'admin-booking', name: 'จองแทนลูกค้า', icon: '👑' },
    { id: 'closures', name: 'วันปิดทำการ', icon: '🚧' },
    { id: 'users', name: 'ผู้ใช้', icon: '👥' },
    { id: 'dashboard', name: 'ภาพรวม', icon: '📊' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'courts':
        return <CourtManagement />;
      case 'court-types':
        return <CourtTypeManagement />;
      case 'bookings':
        return <BookingManagement />;
      case 'admin-booking':
        return <AdminBooking />;
      case 'closures':
        return <ClosureManagement />;
      case 'users':
        return <UserManagement />;
      default:
        return <CourtManagement />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">แผงควบคุมการจองสนาม</h1>
              <p className="text-sm text-gray-600">
                ยินดีต้อนรับ, {adminUser?.displayName || 'ผู้ดูแลระบบ'}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ผู้ดูแลระบบ
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs — horizontally scrollable so nothing falls off small screens */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-6 overflow-x-auto whitespace-nowrap">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AdminDashboard; 